// @ts-check

/**
 * Mapbox map initialization and setup utilities.
 * Handles map creation, controls, and popup initialization.
 */

/**
 * Create and configure a new Mapbox map instance.
 * @param {HTMLElement} container - DOM element to mount map to
 * @param {Object} options - Map configuration options
 * @param {string} options.accessToken - Mapbox access token
 * @param {[number, number]} [options.center=[170, -10]] - Initial center coordinates
 * @param {number} [options.zoom=4] - Initial zoom level
 * @param {number} [options.minZoom=2] - Minimum zoom level
 * @param {number} [options.maxZoom=12] - Maximum zoom level
 * @param {string} [options.style='mapbox://styles/mapbox/satellite-v9'] - Map style URL
 * @param {string} [options.projection='mercator'] - Map projection
 * @returns {Promise<import('mapbox-gl').Map>} Configured Mapbox map instance
 */
export async function createMap(container, options) {
  const mapboxgl = await import("mapbox-gl");

  mapboxgl.default.accessToken = options.accessToken;

  const map = new mapboxgl.default.Map({
    container,
    style: options.style || "mapbox://styles/mapbox/satellite-v9",
    center: options.center || [170, -10],
    zoom: options.zoom || 4,
    minZoom: options.minZoom || 2,
    maxZoom: options.maxZoom || 12,
    projection: options.projection || "mercator",
    dragRotate: false, // Disable right-click drag to rotate
    pitchWithRotate: false // Disable pitch change with rotation
  });

  return map;
}

/**
 * Add navigation controls to map.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {import('mapbox-gl').ControlPosition} [position='bottom-left'] - Control position
 * @returns {Promise<void>}
 */
export async function addNavigationControls(map, position = "bottom-left") {
  const mapboxgl = await import("mapbox-gl");
  map.addControl(
    new mapboxgl.default.NavigationControl({ showCompass: false }),
    position
  );
}

/**
 * Add reset/home control to map.
 * Creates a custom control button that resets the map to initial view.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {() => void} onReset - Callback function to execute when reset is clicked
 * @param {import('mapbox-gl').ControlPosition} [position='bottom-left'] - Control position
 * @returns {Promise<{button: HTMLButtonElement, container: HTMLElement}>} Object containing button and container references
 */
export async function addResetControl(map, onReset, position = "bottom-left") {
  const mapboxgl = await import("mapbox-gl");

  // Create custom control class
  class ResetControl {
    onAdd() {
      const container = document.createElement("div");
      container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

      const button = document.createElement("button");
      button.className = "mapboxgl-ctrl-icon";
      button.type = "button";
      button.setAttribute("aria-label", "Reset map view");
      button.title = "Reset map view";
      button.id = "mapbox-reset-control-button";

      // Use Bootstrap Icons home icon via class name
      const icon = document.createElement("i");
      icon.className = "bi bi-house-door";
      icon.style.fontSize = "18px";

      button.appendChild(icon);
      button.onclick = onReset;

      container.appendChild(button);
      this._container = container;
      this._button = button;
      return container;
    }

    onRemove() {
      if (this._container) {
        this._container.remove();
        this._container = null;
        this._button = null;
      }
    }

    getButton() {
      return this._button;
    }
  }

  const control = new ResetControl();
  map.addControl(control, position);

  return {
    button: control.getButton(),
    container: control._container
  };
}

/**
 * Initialize a complete Mapbox map with standard setup.
 * @param {HTMLElement} container - DOM element to mount map to
 * @param {Object} options - Configuration options
 * @param {string} options.accessToken - Mapbox access token
 * @param {Function} [options.onLoad] - Callback when map is loaded
 * @returns {Promise<{map: import('mapbox-gl').Map}>}
 */
export async function initializeMap(container, options) {
  const map = await createMap(container, options);
  await addNavigationControls(map);

  if (options.onLoad) {
    map.on("load", () => options.onLoad(map));
  }

  return { map };
}
